/**
 * Time parsing hook with natural language support
 * Combines chrono-node with custom time patterns for comprehensive parsing
 */

import { useCallback, useMemo } from 'react'
import { Temporal } from '@js-temporal/polyfill'

import {
  TIME_PATTERNS,
  NAMED_TIMES,
  normalizeInput,
  isValidTime,
  applyBusinessHoursBias,
} from '../utils/timePatterns'
import { createPlainTime, isTimeInRange } from '../utils/temporalHelpers'
import type { ParseResult, ChronoModule, ChronoParser } from '../types'

export interface UseTimeParserOptions {
  /**
   * Parsing mode controls which parsing methods are used:
   * - 'strict': Only pattern-based parsing (HH:MM, H:MMam/pm). No natural language or named times.
   * - 'smart': All parsing methods including patterns, named times ("noon", "morning"),
   *   and natural language expressions ("in 30 minutes", "2 hours from now")
   */
  mode?: 'strict' | 'smart'
  /** Minimum allowed time */
  min?: Temporal.PlainTime
  /** Maximum allowed time */
  max?: Temporal.PlainTime
  /** Default reference date for relative expressions */
  referenceDate?: Date
  /** Timezone for context-aware parsing */
  timezone?: string
  /** Optional chrono-node module for natural language parsing */
  chronoModule?: ChronoModule
}

export interface UseTimeParserReturn {
  /**
   * Parse time from string input and return result with confidence score.
   *
   * Attempts to parse the input using multiple strategies (patterns, named times,
   * natural language) and returns the highest confidence result. The result includes
   * the parsed time, confidence score, success status, and any error messages.
   *
   * @param input - Time string to parse (e.g., "2pm", "14:30", "noon")
   * @returns ParseResult with time, confidence score, and success status
   *
   * @example
   * const result = parseTime("2:30pm")
   * if (result.success) {
   *   console.log(result.time) // Temporal.PlainTime(14, 30)
   *   console.log(result.confidence) // 0.9
   * }
   */
  parseTime: (input: string) => ParseResult

  /**
   * Check if input has valid time format and meets minimum confidence threshold.
   *
   * Uses parseTime internally and validates that the result is successful and
   * meets the configured minConfidence threshold (default: 0.3).
   *
   * @param input - Time string to validate
   * @returns true if input is valid and meets confidence threshold, false otherwise
   *
   * @example
   * // With default minConfidence (0.3)
   * isValidInput("14:30") // true (confidence: 0.9)
   * isValidInput("noon") // true (confidence: 0.5)
   *
   * @example
   * // With high minConfidence (0.7)
   * const { isValidInput } = useTimeParser({ parserConfig: { minConfidence: 0.7 } })
   * isValidInput("14:30") // true (confidence: 0.9)
   * isValidInput("noon") // false (confidence: 0.5 < 0.7)
   */
  isValidInput: (input: string) => boolean
}

/**
 * Convert the reference date to the target timezone for proper chrono parsing context.
 * This ensures that relative time expressions are parsed correctly in the target timezone.
 *
 * The timezone parameter is pre-validated by useSmartTimeCombobox using isValidTimezone()
 * and will only contain valid IANA timezone identifiers or undefined.
 */
const getTzAwareReferenceDate = (
  referenceDate: Date,
  timezone?: string,
): Date => {
  if (!timezone) return referenceDate
  const zdt = Temporal.Instant.fromEpochMilliseconds(
    referenceDate.getTime(),
  ).toZonedDateTimeISO(timezone)
  return new Date(zdt.epochMilliseconds)
}

/**
 * Hook for parsing time input strings with configurable confidence thresholds.
 *
 * Provides time parsing utilities that support multiple input formats including
 * 12/24-hour times, named times (e.g., "noon"), and natural language expressions.
 * Each parse result includes a confidence score that can be used to validate
 * input quality.
 *
 * @param options - Parser configuration options
 * @param options.mode - Parsing strictness: 'strict' for exact matches only, 'smart' for fuzzy matching
 * @param options.step - Step interval in minutes for rounding suggestions (default: 15)
 * @param options.roundToStep - Whether parsed times should be rounded to the configured step (default: true)
 * @param options.min - Minimum allowed time (Temporal.PlainTime)
 * @param options.max - Maximum allowed time (Temporal.PlainTime)
 * @param options.referenceDate - Reference date for relative time expressions (default: new Date())
 * @param options.timezone - Timezone identifier for context-aware parsing (IANA format)
 * @param options.parserConfig - Parser-specific configuration
 * @param options.parserConfig.minConfidence - Minimum confidence threshold (0-1) for valid parses. Default: 0.3
 *
 * @returns Parser utilities
 * @returns parseTime - Parses time string and returns result with confidence score
 * @returns isValidInput - Validates if input meets minimum confidence threshold
 *
 * @example
 * // Basic usage
 * const { parseTime, isValidInput } = useTimeParser({
 *   mode: 'smart',
 * })
 *
 * const result = parseTime("2:30pm")
 * if (result.success) {
 *   console.log(result.time) // Temporal.PlainTime(14, 30)
 *   console.log(result.confidence) // 0.9 (high confidence)
 * }
 *
 * @example
 * // Custom confidence threshold
 * const { parseTime, isValidInput } = useTimeParser({
 *   mode: 'smart',
 *   parserConfig: { minConfidence: 0.7 } // Only accept high-confidence parses
 * })
 *
 * // High confidence parse (0.9) - accepted
 * isValidInput("14:30") // true
 *
 * // Low confidence parse (0.5) - rejected
 * isValidInput("noon") // false
 *
 * @example
 * // With constraints and custom confidence
 * const { parseTime, isValidInput } = useTimeParser({
 *   mode: 'smart',
 *   min: new Temporal.PlainTime(9, 0),
 *   max: new Temporal.PlainTime(17, 0),
 *   parserConfig: { minConfidence: 0.8 }
 * })
 *
 * const result = parseTime("2:30pm")
 * if (result.success && result.confidence >= 0.9) {
 *   // Very high confidence parse within business hours
 * }
 */
export function useTimeParser(
  options: UseTimeParserOptions = {},
): UseTimeParserReturn {
  const {
    mode = 'smart',
    min,
    max,
    referenceDate = new Date(),
    timezone,
    chronoModule,
  } = options

  const minConfidence = 0.3

  // Configure chrono parser only if chronoModule is provided
  const chronoParser = useMemo((): ChronoParser | null => {
    if (!chronoModule) return null

    const parser = chronoModule.casual.clone()

    // Customize chrono for time-only parsing
    parser.refiners.push({
      refine: (_context, results) => {
        // Focus on time components only
        return results.filter(
          (result) =>
            result.start.isCertain('hour') || result.start.isCertain('minute'),
        )
      },
    })

    return parser
  }, [chronoModule])

  const ensureConstraints = useCallback(
    (
      time: Temporal.PlainTime,
      originalInput: string,
    ):
      | { ok: true; time: Temporal.PlainTime }
      | { ok: false; result: ParseResult } => {
      const outOfRangeResult: ParseResult = {
        time: null,
        confidence: 0,
        success: false,
        error: 'Time is outside allowed range',
        originalInput,
      }

      if (min || max) {
        if (!isTimeInRange(time, min, max)) {
          return { ok: false, result: outOfRangeResult }
        }
      }

      return { ok: true, time }
    },
    [min, max],
  )

  /**
   * Parse time using custom patterns
   */
  const parseWithPatterns = useCallback(
    (input: string): ParseResult => {
      const normalized = normalizeInput(input)

      // Try each pattern in order of confidence
      for (const pattern of TIME_PATTERNS) {
        const match = normalized.match(pattern.pattern)
        if (match) {
          const result = pattern.parser(match)
          if (result && isValidTime(result.hour, result.minute)) {
            let { hour, minute } = result
            let computedConfidence = pattern.confidence

            // Apply business hours bias for ambiguous input
            if (mode === 'smart' && pattern.confidence < 1.0) {
              const biased = applyBusinessHoursBias(hour, minute)
              hour = biased.hour
              minute = biased.minute
              computedConfidence = Math.max(
                pattern.confidence,
                biased.confidence,
              )
            }

            const initial = createPlainTime(hour, minute)
            const constraintResult = ensureConstraints(initial, input)
            if (!constraintResult.ok) {
              return constraintResult.result
            }

            return {
              time: constraintResult.time,
              confidence: computedConfidence,
              success: true,
              originalInput: input,
            }
          }
        }
      }

      return {
        time: null,
        confidence: 0,
        success: false,
        error: 'No pattern match found',
        originalInput: input,
      }
    },
    [mode, ensureConstraints],
  )

  /**
   * Parse named time expressions
   */
  const parseNamedTime = useCallback(
    (input: string): ParseResult => {
      const normalized = normalizeInput(input)
      const namedTime = NAMED_TIMES[normalized]

      if (namedTime) {
        const initial = createPlainTime(namedTime.hour, namedTime.minute)
        const constraintResult = ensureConstraints(initial, input)
        if (!constraintResult.ok) {
          return constraintResult.result
        }

        return {
          time: constraintResult.time,
          confidence: namedTime.confidence,
          success: true,
          originalInput: input,
        }
      }

      return {
        time: null,
        confidence: 0,
        success: false,
        error: 'Named time not recognized',
        originalInput: input,
      }
    },
    [ensureConstraints],
  )

  /**
   * Parse using chrono-node for natural language expressions
   */
  const parseWithChrono = useCallback(
    (input: string): ParseResult => {
      if (!chronoParser) {
        return {
          time: null,
          confidence: 0,
          success: false,
          error: 'Natural language parsing not available',
          originalInput: input,
        }
      }

      try {
        const reference = getTzAwareReferenceDate(referenceDate, timezone)
        const results = chronoParser.parse(input, reference)
        if (results.length === 0) {
          return {
            time: null,
            confidence: 0,
            success: false,
            error: 'Natural language parsing failed',
            originalInput: input,
          }
        }
        const first = results[0]
        if (!first) {
          return {
            time: null,
            confidence: 0,
            success: false,
            error: 'Natural language parsing failed',
            originalInput: input,
          }
        }
        const hour = first.start.get('hour')
        const minute = first.start.get('minute') ?? 0
        if (hour === null || !isValidTime(hour, minute)) {
          return {
            time: null,
            confidence: 0,
            success: false,
            error: 'Natural language parsing failed',
            originalInput: input,
          }
        }
        const parsedTime = createPlainTime(hour, minute)
        const constraintResult = ensureConstraints(parsedTime, input)
        if (!constraintResult.ok) {
          return constraintResult.result
        }
        const confidence = first.start.isCertain('minute') ? 0.8 : 0.6
        return {
          time: constraintResult.time,
          confidence,
          success: true,
          originalInput: input,
        }
      } catch {
        return {
          time: null,
          confidence: 0,
          success: false,
          error: 'Natural language parsing failed',
          originalInput: input,
        }
      }
    },
    [chronoParser, ensureConstraints, referenceDate, timezone],
  )

  /**
   * Main parsing function that tries all methods based on `mode` setting.
   */
  const parseTime = useCallback(
    (input: string): ParseResult => {
      if (!input || input.trim().length === 0) {
        return {
          time: null,
          confidence: 0,
          success: false,
          error: 'Empty input',
          originalInput: input,
        }
      }

      const trimmedInput = input.trim()

      // In strict mode, only use pattern-based parsing
      // In smart mode, try all parsing methods (including chrono if available)
      const methods =
        mode === 'strict'
          ? [parseWithPatterns]
          : chronoParser
            ? [parseWithPatterns, parseNamedTime, parseWithChrono]
            : [parseWithPatterns, parseNamedTime]

      let bestResult: ParseResult = {
        time: null,
        confidence: 0,
        success: false,
        error: 'Unable to parse time',
        originalInput: input,
      }

      for (const method of methods) {
        const result = method(trimmedInput)
        if (result.success && result.confidence > bestResult.confidence) {
          bestResult = result

          // If we have a high confidence result, use it
          if (result.confidence >= 0.9) {
            break
          }
        } else if (
          !result.success &&
          result.error === 'Time is outside allowed range' &&
          bestResult.error !== 'Time is outside allowed range'
        ) {
          bestResult = result
        }
      }

      return bestResult
    },
    [mode, parseWithPatterns, parseNamedTime, parseWithChrono, chronoParser],
  )

  /**
   * Check if input appears to be a valid time format
   */
  const isValidInput = useCallback(
    (input: string): boolean => {
      const result = parseTime(input)
      return result.success && result.confidence >= minConfidence
    },
    [minConfidence, parseTime],
  )

  return {
    parseTime,
    isValidInput,
  }
}
